import { decodeHTML } from 'entities';
import { useDataLayerStore } from '~/store/dataLayer';
import { type PageInfo } from '~/types/dataLayer';
import { type WindowIncludingLoadedScript } from '~/types';
import { useDataLayerEvent } from '~/composables/useDataLayerEvent';
import useSkipModules from '~/composables/useSkipModules';

export default defineNuxtPlugin((nuxt) => {
  const { skipModule, moduleNames } = useSkipModules(nuxt._route);
  if (skipModule(moduleNames.ADOBE)) { return; }

  const windowWithLoadedScript = window as WindowIncludingLoadedScript;
  windowWithLoadedScript.loadedScripts = windowWithLoadedScript.loadedScripts || {};
  windowWithLoadedScript.loadedScripts.satelliteScript = ref(false);

  useDataLayerStore().setEnvironmentInfo();

  const runtimeConfig = useRuntimeConfig();
  const colorMode = useColorMode();
  const getPageInfo = () => {
    const dataLayout = document.documentElement.getAttribute('datalayout');
    const pageInfo = {
      pageVisibility: document.visibilityState,
      title: decodeHTML(document.title.replace(runtimeConfig.public?.seo?.postfix || '', '')),
      url: window.location?.href || '',
      publication: runtimeConfig.public.name || '',
      dataLayout: dataLayout && dataLayout.length > 0 ? dataLayout : 'default',
      pageType: 'page',
      referrer: nuxt?.$router?.currentRoute?.value?.meta?.referrer,
      autorefresh: +localStorage.getItem('autoPageRefresh') || (localStorage.getItem('refreshScriptFlag') === 'true' ? 1 : 0)
    } as PageInfo;

    if (colorMode) {
      pageInfo.modeName = colorMode.value;
      pageInfo.modeType = colorMode.preference === 'system' ? 'system' : 'manual';
    }

    return pageInfo;
  };

  if (colorMode) {
    /*
     * colorMode exposes two scalar refs (value, preference). Watching the
     * whole object with deep:true forced a reactive proxy walk on every
     * change; tracking the scalars directly is cheaper and equivalent.
     */
    watch(
      () => [colorMode.value, colorMode.preference],
      ([value, preference]) => {
        const watchPageInfo = useDataLayerStore().pageInfo;
        watchPageInfo.modeName = value;
        watchPageInfo.modeType = preference === 'system' ? 'system' : 'manual';
        useDataLayerStore().setPageInfo(watchPageInfo);
      }
    );
  }

  nuxt.hook('cpp:data:loaded', async ({ type }) => {
    const defPageInfo = { ...getPageInfo(), pageType: type };
    const pageInfo = await nuxt.callHook('datalayer:pageInfo', { pageInfo: defPageInfo }) || defPageInfo;
    useDataLayerStore().setPageInfo(pageInfo);
    useDataLayerEvent().pageView();
    nuxt.callHook('datalayer:loaded');
  });

  const rtConfPub = useRuntimeConfig().public;

  if (rtConfPub?.dataLayer?.trackingScript) {
    useHead({
      script: [
        {
          id: 'data-layer',
          src: `${rtConfPub.appEnv === 'prod' ? 'https://stc.dngroup.com' : 'https://stc-test.dngroup.com'}/tracking-lib/scripts/default.js?cpp=true&adobe=proxy`,
          async: true,
          onload: () => {
            windowWithLoadedScript.loadedScripts.satelliteScript.value = true;
          }
        }
      ]
    });
  }
});
